/**
 * Modal de dissociation en lot des copies par matière + formateur.
 *
 * Workflow utilisateur :
 *   1. Sélectionner une matière (autocomplete).
 *   2. (Optionnel) Filtrer par formateur (peuplé dynamiquement avec les
 *      formateurs ayant des copies associées sur cette matière).
 *   3. Voir la liste des copies associées (code + matricule + nom étudiant + classe).
 *   4. Cocher individuellement OU tout sélectionner.
 *   5. Cliquer « Dissocier la sélection » → confirmation → action en lot.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Search, AlertTriangle, Loader, CheckCircle2, Trash2, Users, BookOpen,
} from 'lucide-react';
import { useGetMatieresQuery } from '../../services/matieresApi';
import {
  useLazyGetCopiesAssocieesParMatiereQuery,
  useDissocierLotCopiesMutation,
} from '../../services/copiesApi';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Notification de succès remontée au parent. */
  onSuccess?: (msg: string) => void;
}

const DissocierLotModal: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  // Sélections
  const [matiereSearch, setMatiereSearch] = useState('');
  const [matiereId, setMatiereId] = useState<number | null>(null);
  const [matiereLabel, setMatiereLabel] = useState('');
  const [enseignantId, setEnseignantId] = useState<number | null>(null);

  // Sélection des copies à dissocier
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [searchEtudiant, setSearchEtudiant] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [commentaire, setCommentaire] = useState('');

  const { data: matieres = [] } = useGetMatieresQuery();
  const [fetchCopies, { data, isFetching }] = useLazyGetCopiesAssocieesParMatiereQuery();
  const [dissocierLot, { isLoading: dissociating }] = useDissocierLotCopiesMutation();

  // Recharger quand matière/formateur change
  useEffect(() => {
    if (open && matiereId) {
      fetchCopies({ matiere_id: matiereId, enseignant_id: enseignantId ?? undefined });
      setSelection(new Set());
    }
  }, [open, matiereId, enseignantId, fetchCopies]);

  // Recharger quand le modal s'ouvre (rester propre)
  useEffect(() => {
    if (!open) {
      setMatiereId(null);
      setMatiereLabel('');
      setMatiereSearch('');
      setEnseignantId(null);
      setSelection(new Set());
      setSearchEtudiant('');
      setConfirmOpen(false);
      setCommentaire('');
    }
  }, [open]);

  // Liste matières filtrées (autocomplete)
  const matieresFiltrees = useMemo(() => {
    if (!matiereSearch || matiereSearch.length < 2) return [];
    const q = matiereSearch.toLowerCase();
    return (matieres as any[])
      .filter((m: any) =>
        (m.nom_matiere || '').toLowerCase().includes(q) ||
        (m.code_matiere || '').toLowerCase().includes(q),
      )
      .slice(0, 12);
  }, [matieres, matiereSearch]);

  // Liste copies filtrées par recherche étudiant
  const copiesFiltrees = useMemo(() => {
    if (!data?.copies) return [];
    if (!searchEtudiant.trim()) return data.copies;
    const q = searchEtudiant.toLowerCase();
    return data.copies.filter(c =>
      (c.code_copie || '').toLowerCase().includes(q) ||
      (c.etudiant_matricule || '').toLowerCase().includes(q) ||
      (c.etudiant_nom || '').toLowerCase().includes(q) ||
      (c.etudiant_prenoms || '').toLowerCase().includes(q) ||
      (c.etudiant_classe || '').toLowerCase().includes(q),
    );
  }, [data, searchEtudiant]);

  const allSelected = copiesFiltrees.length > 0 && copiesFiltrees.every(c => selection.has(c.id));
  const someSelected = copiesFiltrees.some(c => selection.has(c.id));

  const toggleAll = () => {
    if (allSelected) {
      // Désélectionner uniquement les filtrées
      const next = new Set(selection);
      copiesFiltrees.forEach(c => next.delete(c.id));
      setSelection(next);
    } else {
      const next = new Set(selection);
      copiesFiltrees.forEach(c => next.add(c.id));
      setSelection(next);
    }
  };

  const toggleOne = (id: number) => {
    const next = new Set(selection);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelection(next);
  };

  const handleConfirmDissocier = async () => {
    if (selection.size === 0) return;
    try {
      const res = await dissocierLot({
        ids: Array.from(selection),
        commentaire: commentaire || `Dissociation en lot — matière ${matiereLabel}`,
      }).unwrap();
      onSuccess?.(res.message || `${res.dissociees} copie(s) dissociée(s)`);
      // Recharger la liste
      if (matiereId) {
        fetchCopies({ matiere_id: matiereId, enseignant_id: enseignantId ?? undefined });
      }
      setSelection(new Set());
      setConfirmOpen(false);
      setCommentaire('');
    } catch (e: any) {
      alert(e?.data?.error || 'Erreur lors de la dissociation');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Trash2 size={20} className="text-red-600 dark:text-red-400" />
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Dissocier les copies en lot
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Sélectionne une matière (et optionnellement un formateur), coche les copies à dissocier
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={18} />
          </button>
        </div>

        {/* Filtres : Matière + Formateur */}
        <div className="px-5 py-4 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700 space-y-3">
          {/* Matière (autocomplete) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
              <BookOpen size={12} /> Matière
            </label>
            <div className="relative">
              <input
                type="text"
                value={matiereSearch}
                onChange={(e) => { setMatiereSearch(e.target.value); setMatiereId(null); }}
                placeholder="Tapez le nom ou code de la matière..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              {matieresFiltrees.length > 0 && !matiereId && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {matieresFiltrees.map((m: any) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => {
                        setMatiereId(m.id);
                        setMatiereLabel(`${m.code_matiere} — ${m.nom_matiere}`);
                        setMatiereSearch(`${m.code_matiere} — ${m.nom_matiere}`);
                        setEnseignantId(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30"
                    >
                      <span className="font-mono text-xs text-gray-500">{m.code_matiere}</span>
                      <span className="ml-2">{m.nom_matiere}</span>
                      {m.niveau && <span className="ml-2 text-xs text-gray-400">[{m.niveau}]</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Formateur (peuplé dynamiquement) */}
          {data && data.enseignants_disponibles.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                <Users size={12} /> Filtrer par formateur (optionnel)
                {data.enseignants_disponibles.length > 1 && (
                  <span className="text-[10px] text-blue-600 dark:text-blue-400 font-normal ml-1">
                    ({data.enseignants_disponibles.length} formateurs trouvés)
                  </span>
                )}
              </label>
              <select
                value={enseignantId ?? ''}
                onChange={(e) => setEnseignantId(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">— Tous les formateurs —</option>
                {data.enseignants_disponibles.map(e => (
                  <option key={e.id} value={e.id}>{e.nom_complet}</option>
                ))}
              </select>
            </div>
          )}

          {/* Indicateur : matière trouvée dans plusieurs options/IDs */}
          {data && data.matiere_ids_equivalentes && data.matiere_ids_equivalentes.length > 1 && (
            <div className="flex items-start gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded text-[11px] text-blue-800 dark:text-blue-200">
              <BookOpen size={12} className="mt-0.5 shrink-0" />
              <span>
                <strong>{data.matiere_nom}</strong> existe dans <strong>{data.matiere_ids_equivalentes.length} options/IDs</strong>
                {' '}au niveau <strong>{data.niveau}</strong>. Toutes les copies des variantes sont affichées,
                tous les formateurs sont listés ci-dessus.
              </span>
            </div>
          )}
        </div>

        {/* Recherche dans la liste + barre actions */}
        {matiereId && (
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
              <input
                type="text"
                value={searchEtudiant}
                onChange={(e) => setSearchEtudiant(e.target.value)}
                placeholder="Rechercher (code, matricule, nom, classe)..."
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {data ? `${copiesFiltrees.length} copie(s) affichée(s) · ${selection.size} sélectionnée(s)` : '—'}
            </div>
          </div>
        )}

        {/* Liste copies */}
        <div className="flex-1 overflow-y-auto">
          {!matiereId ? (
            <div className="p-12 text-center text-sm text-gray-400">
              <BookOpen size={32} className="mx-auto mb-3 opacity-30" />
              Sélectionne d'abord une matière pour voir les copies associées.
            </div>
          ) : isFetching ? (
            <div className="p-12 text-center">
              <Loader size={28} className="animate-spin mx-auto text-blue-600" />
              <p className="mt-2 text-sm text-gray-500">Chargement…</p>
            </div>
          ) : copiesFiltrees.length === 0 ? (
            <div className="p-12 text-center text-sm text-gray-400">
              <CheckCircle2 size={32} className="mx-auto mb-3 text-gray-300" />
              {data && data.total > 0
                ? 'Aucune copie ne correspond à la recherche.'
                : 'Aucune copie associée pour cette matière' + (enseignantId ? ' avec ce formateur.' : '.')}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-700/30 sticky top-0">
                <tr>
                  <th className="w-8 px-2 py-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                      onChange={toggleAll}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Code copie</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Matricule</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Étudiant</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Classe</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Option</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Formateur</th>
                  <th className="px-2 py-2 text-center font-semibold text-gray-600 dark:text-gray-300">Notée</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {copiesFiltrees.map(c => {
                  const checked = selection.has(c.id);
                  return (
                    <tr
                      key={c.id}
                      className={checked ? 'bg-red-50/50 dark:bg-red-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}
                    >
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOne(c.id)}
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                      </td>
                      <td className="px-2 py-1.5 font-mono text-gray-700 dark:text-gray-300">{c.code_copie}</td>
                      <td className="px-2 py-1.5 font-mono text-gray-500">{c.etudiant_matricule || '—'}</td>
                      <td className="px-2 py-1.5 text-gray-800 dark:text-gray-200">
                        {c.etudiant_nom?.toUpperCase()} {c.etudiant_prenoms}
                      </td>
                      <td className="px-2 py-1.5 text-gray-500">{c.etudiant_classe || '—'}</td>
                      <td className="px-2 py-1.5 text-gray-500 truncate max-w-[140px]" title={c.matiere_option || ''}>
                        {c.matiere_option || '—'}
                      </td>
                      <td className="px-2 py-1.5 text-gray-500 truncate max-w-[180px]" title={c.enseignant_nom || ''}>
                        {c.enseignant_nom || '—'}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {c.est_notee
                          ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded" title="Cette copie a déjà une note enregistrée — la dissocier ne supprime pas la note de NoteExamen">
                              <AlertTriangle size={9} /> Notée
                            </span>
                          : <span className="text-gray-300">—</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 rounded-b-xl flex-wrap gap-2">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {selection.size > 0 && (
              <span className="font-semibold text-red-700 dark:text-red-300">
                {selection.size} copie(s) sélectionnée(s)
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Fermer
            </button>
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={selection.size === 0 || dissociating}
              className="inline-flex items-center px-4 py-2 text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-md font-medium"
            >
              {dissociating ? <Loader size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
              Dissocier la sélection ({selection.size})
            </button>
          </div>
        </div>

        {/* Confirmation modale */}
        {confirmOpen && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-xl">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-5 max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-700">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle size={24} className="text-red-500 shrink-0" />
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Confirmer la dissociation
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Vous allez dissocier <strong>{selection.size}</strong> copie(s) pour la matière
                    <br /><strong className="text-gray-900 dark:text-gray-100">{matiereLabel}</strong>
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    Les notes éventuellement saisies via NoteExamen ne sont PAS supprimées —
                    seul le lien copie ↔ étudiant est rompu. Les copies pourront être ré-associées.
                  </p>
                </div>
              </div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Commentaire (optionnel)
              </label>
              <input
                type="text"
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                placeholder="Raison de la dissociation..."
                className="w-full mb-4 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setConfirmOpen(false)}
                  disabled={dissociating}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmDissocier}
                  disabled={dissociating}
                  className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded inline-flex items-center"
                >
                  {dissociating ? <Loader size={12} className="animate-spin mr-1" /> : <Trash2 size={12} className="mr-1" />}
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DissocierLotModal;
